import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const LuxuryNoir = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-luxury-noir">

      {/* ── Solid background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Diagonal split overlay (top-left dark wedge) ── */}
      <svg
        style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}
        width="420" height="560" viewBox="0 0 420 560"
      >
        <polygon
          points="0,0 260,0 0,320"
          fill="var(--theme-accent)"
          opacity="0.09"
        />
        <polygon
          points="420,560 160,560 420,240"
          fill="var(--theme-accent)"
          opacity="0.09"
        />
        {/* thin gold diagonal lines */}
        <line x1="0" y1="340" x2="420" y2="0"   stroke="var(--theme-accent)" strokeWidth="0.6" opacity="0.25" />
        <line x1="0" y1="380" x2="420" y2="40"  stroke="var(--theme-accent)" strokeWidth="0.4" opacity="0.15" />
        <line x1="0" y1="420" x2="420" y2="80"  stroke="var(--theme-accent)" strokeWidth="0.4" opacity="0.10" />
      </svg>

      {/* ── Left accent bar ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, width: 5, bottom: 0,
        background: "var(--theme-accent)", zIndex: 10,
      }} />

      {/* ── Right thin accent bar ── */}
      <div style={{
        position: "absolute", top: 0, right: 0, width: 2, bottom: 0,
        background: "var(--theme-accent)", opacity: 0.35, zIndex: 10,
      }} />

      {/* ── Corner diamond ornaments ── */}
      {[
        { top: 10, right: 14 },
        { bottom: 10, left: 14 },
      ].map((pos, i) => (
        <svg key={i} style={{ position: "absolute", ...pos, zIndex: 3, opacity: 0.45 }}
          width="18" height="18" viewBox="0 0 18 18">
          <polygon points="9,1 17,9 9,17 1,9"
            fill="none" stroke="var(--theme-accent)" strokeWidth="1.5" />
          <polygon points="9,5 13,9 9,13 5,9"
            fill="var(--theme-accent)" opacity="0.5" />
        </svg>
      ))}

      {/* ── Content wrapper ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "14px 20px 14px 22px",
      }}>

        {/* ── Top brand bar ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 22, height: 22, objectFit: "contain", borderRadius: 2 }} />
            )}
            {isVisible("organizationName") && (
              <span style={{
                fontFamily: "var(--theme-heading-font,'Cormorant Garamond',serif)",
                fontSize: "0.82rem", fontWeight: 700,
                color: "var(--theme-text)",
                textTransform: "uppercase", letterSpacing: "0.22em",
              }}>
                {(buyer?.organizationName || "MAISON").toUpperCase()}
              </span>
            )}
          </div>
          {/* roman numeral-style edition mark */}
          <span style={{
            fontSize: "0.52rem", letterSpacing: "0.18em",
            color: "var(--theme-accent)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            fontWeight: 600,
          }}>
            {product?.segment?.toUpperCase() || "EDITION"}
          </span>
        </div>

        {/* ── Horizontal rule pair ── */}
        <div style={{ position: "relative", marginBottom: 10, height: 6 }}>
          <div style={{ height: 1, background: "var(--theme-accent)", opacity: 0.6 }} />
          <div style={{ height: 1, background: "var(--theme-accent)", opacity: 0.25, marginTop: 4 }} />
        </div>

        {/* ── Hero image area (full-bleed center) ── */}
        <div style={{
          position: "relative",
          flex: "0 0 200px",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 10,
        }}>
          {/* spotlight circle */}
          <div style={{
            position: "absolute",
            width: 200, height: 200, borderRadius: "50%",
            background: "radial-gradient(circle, var(--theme-accent) 0%, transparent 70%)",
            opacity: 0.08,
          }} />
          {/* double-ring frame */}
          <div style={{
            position: "absolute",
            width: 190, height: 190, borderRadius: "50%",
            border: "1px solid var(--theme-accent)", opacity: 0.2,
          }} />
          <div style={{
            position: "absolute",
            width: 170, height: 170, borderRadius: "50%",
            border: "1px solid var(--theme-accent)", opacity: 0.12,
          }} />
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "72%", maxHeight: "100%", objectFit: "contain",
              position: "relative", zIndex: 2,
              filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.55))",
            }}
          />
        </div>

        {/* ── Product name ── */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Cormorant Garamond',serif)",
            fontSize: "1.5rem", fontWeight: 400, fontStyle: "italic",
            color: "var(--theme-text)", lineHeight: 1.15, letterSpacing: "0.05em",
          }}>
            {product?.name || "Fine Jewellery"}
          </div>
          {/* accent dash */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, marginTop: 6,
          }}>
            <div style={{ width: 28, height: 1, background: "var(--theme-accent)", opacity: 0.5 }} />
            <div style={{ width: 6, height: 6, background: "var(--theme-accent)", transform: "rotate(45deg)", opacity: 0.7 }} />
            <div style={{ width: 28, height: 1, background: "var(--theme-accent)", opacity: 0.5 }} />
          </div>
        </div>

        {/* ── Attributes grid ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 10px",
            border: "1px solid var(--theme-border)",
            borderLeft: "3px solid var(--theme-accent)",
            borderRadius: 2, padding: "6px 10px", marginBottom: 8,
            background: "var(--theme-surface)",
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.48rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={22} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.64rem", fontWeight: 600, color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>{fmtVal(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price + CTA ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 8,
        }}>
          <div>
            <div style={{
              fontSize: "0.5rem", fontWeight: 700, letterSpacing: "0.18em",
              color: "var(--theme-textMuted)", textTransform: "uppercase",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)", marginBottom: 1,
            }}>
              RETAIL PRICE
            </div>
            <div style={{
              fontFamily: "var(--theme-heading-font,'Cormorant Garamond',serif)",
              fontSize: "1.4rem", fontWeight: 700,
              color: "var(--theme-accent)", lineHeight: 1,
            }}>
              {formatPrice(price)}
            </div>
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.6rem", fontWeight: 800,
            padding: "8px 16px", letterSpacing: "0.12em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)",
          }}>
            ENQUIRE NOW
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--theme-border)", paddingTop: 5,
          fontSize: "0.5rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          letterSpacing: "0.08em",
        }}>
          {isVisible("address") && <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>}
          {isVisible("phone") && <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>}
        </div>
      </div>
    </div>
  );
});

LuxuryNoir.displayName = "LuxuryNoir";
export default LuxuryNoir;
